import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

interface CalendarDayItem {
  dayNumber: number | null;
  dateStr: string | null;
  isToday: boolean;
  hasTasks: boolean;
}

interface TaskCalendarWidgetProps {
  calendarViewDate: Date;
  calendarMonthName: string;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  calendarDays: CalendarDayItem[];
  customDate: string;
  onDayClick: (dateStr: string | null) => void;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function TaskCalendarWidget({
  calendarMonthName,
  onPrevMonth,
  onNextMonth,
  calendarDays,
  customDate,
  onDayClick,
}: TaskCalendarWidgetProps) {
  return (
    <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 p-5 shadow-xs flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-indigo-500" />
          <h4 className="text-base font-bold text-slate-900 dark:text-white tracking-tight">Task Calendar</h4>
        </div>

        {/* Month Navigator */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onPrevMonth}
            className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 dark:text-slate-400 transition-colors cursor-pointer"
            title="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs font-bold text-slate-800 dark:text-slate-200 min-w-[90px] text-center">
            {calendarMonthName}
          </span>
          <button
            type="button"
            onClick={onNextMonth}
            className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 dark:text-slate-400 transition-colors cursor-pointer"
            title="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((day) => (
          <span key={day} className="text-[12px] font-bold text-slate-400 dark:text-slate-500">
            {day}
          </span>
        ))}
      </div>

      {/* Calendar Days Grid */}
      <div className="grid grid-cols-7 gap-1 text-center">
        {calendarDays.map((cell, idx) => {
          if (cell.dayNumber === null) {
            return <div key={`empty-${idx}`} className="h-8 w-8" />;
          }

          const isSelected = customDate === cell.dateStr;
          return (
            <button
              key={cell.dateStr}
              type="button"
              onClick={() => onDayClick(cell.dateStr)}
              className={`h-8 w-8 mx-auto rounded-full flex flex-col items-center justify-center relative transition-all text-sm font-bold cursor-pointer ${
                isSelected
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : cell.isToday
                    ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-300 font-extrabold'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10'
              }`}
            >
              <span>{cell.dayNumber}</span>
              {cell.hasTasks && !isSelected && (
                <span className="h-1 w-1 rounded-full bg-emerald-500 absolute bottom-1" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
